import { FormSpy, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import { getKnoSupervisionStartAvailableUnplannedActivities } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';

import styles from './ErknmFieldMark.module.scss';
import { erknmFieldsMarkedAsRequired } from '../common/erknmFieldsMarkedAsRequired';
import { isFieldRequiredForNextStatusesInFillInProcess } from '../common/fieldsRequiredForNextStatusesInFillInProcess';
import { isSectionRequiredForNextStatusesInFillInProcess } from '../common/sectionsRequiredForNextStatusesInFillInProcess';
import {
  getKnmData,
  getKnmDataAgreementOnApproval,
  getKnmIsErknmStatusApproving,
  getKnmIsErknmStatusCommentsExist,
  getKnmIsErknmStatusFillInProcess,
  getKnmIsErknmStatusReadyForApprove,
  getKnmIsErpStatusTypeCarriedOut,
  getKnmIsErpStatusTypeWaitingCarryOut,
  getKnmSystemType,
} from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { isLaterThanAdminSetting } from '../../../CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from '../../../APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import {
  getUserRoleIsAdmin,
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
} from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { isSectionRequiredForNextStatusesInApproving } from '../common/sectionsRequiredForNextStatusesInApproving';
import { isFieldRequiredForNextStatusesInTypeCarriedOut } from '../common/fieldsRequiredForNextStatusesInTypeCarriedOut';
import { IKnmForm } from '../../../KnmPackage/MainModule/interfaces/IKnmForm';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { isFieldRequiredForNextStatusesInReadyForApprove } from '../common/fieldsRequiredForNextStatusesInReadyForApprove';
import { isFieldRequiredForNextStatusesInTypeWaitingCarriedOut } from '../common/fieldsRequiredForNextStatusesInTypeWaitingCarriedOut';

interface IErknmFieldMarkProps {
  fieldName?: string;
  sectionName?: string;
}

const idRegex = /\[\d+]/gi;

export const ErknmFieldMark = ({ fieldName, sectionName }: IErknmFieldMarkProps) => {
  const form = useForm();
  const isSupervisor = useSelector(getUserRoleIsSupervisor);
  const isProsecutorOrSuperProsecutor = useSelector(getUserRoleIsProsecutorOrSuperProsecutor);
  const isAdmin = useSelector(getUserRoleIsAdmin);
  const isKnmStatusFillInProcess = useSelector(getKnmIsErknmStatusFillInProcess);
  const isKnmStatusCommentsExist = useSelector(getKnmIsErknmStatusCommentsExist);
  const isKnmStatusReadyForApprove = useSelector(getKnmIsErknmStatusReadyForApprove);
  const isKnmStatusApproving = useSelector(getKnmIsErknmStatusApproving);
  const isAgreementOnApproval = useSelector(getKnmDataAgreementOnApproval);
  const isKnmStatusTypeWaitingCarriedOut = useSelector(getKnmIsErpStatusTypeWaitingCarryOut);
  const isKnmStatusTypeCarriedOut = useSelector(getKnmIsErpStatusTypeCarriedOut);
  const knmSystemType = useSelector(getKnmSystemType);
  const isAvailableUnplannedActivities = useSelector(getKnoSupervisionStartAvailableUnplannedActivities);
  const knmData = useSelector(getKnmData);
  const created = knmData?.created || undefined;
  const settings = useSelector(getAdminFetchSettings);
  const isLaterThanSetting = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.FIELDS_MARKS_481_4_1_14);
  const isEnablePaperlessPassportDocument = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.ENABLED_PAPERLESS_PASSPORT_4_1_3_STARTS_FROM,
  );

  const isShowMark =
    isLaterThanSetting && (knmSystemType === KnmSystemTypesEnum.ERKNM || knmSystemType === KnmSystemTypesEnum.PM);

  if (!isShowMark && !isAdmin) {
    return null;
  }

  const fieldNameTemplate = fieldName?.replaceAll(idRegex, '[{id}]');

  return (
    <FormSpy subscription={{ values: true, errors: true, touched: true }}>
      {props => {
        const isShowRequiredMark =
          (isSupervisor ||
            (isProsecutorOrSuperProsecutor &&
              (isKnmStatusApproving ||
                isAgreementOnApproval ||
                (fieldNameTemplate || '').indexOf('agreements[{id}].terminate') > -1)) ||
            isAdmin) &&
          !!fieldNameTemplate &&
          erknmFieldsMarkedAsRequired.includes(fieldNameTemplate);
        const isShowFillInProcessMark =
          isSupervisor &&
          isKnmStatusFillInProcess &&
          (isFieldRequiredForNextStatusesInFillInProcess(fieldName, props.values, !!isAvailableUnplannedActivities) ||
            isSectionRequiredForNextStatusesInFillInProcess(sectionName, props.values));
        // для статуса "Есть замечания" требования отсутствуют, поэтому берем как для статуса "В процессе заполнения"
        const isShowCommentsExistMark =
          isSupervisor &&
          isKnmStatusCommentsExist &&
          (isFieldRequiredForNextStatusesInFillInProcess(fieldName, props.values, !!isAvailableUnplannedActivities) ||
            isSectionRequiredForNextStatusesInFillInProcess(sectionName, props.values));
        const isShowReadyForApproveMark =
          isSupervisor &&
          isKnmStatusReadyForApprove &&
          isFieldRequiredForNextStatusesInReadyForApprove(fieldName, props.values, !!isAvailableUnplannedActivities);
        const isShowApprovingMark =
          isSupervisor && isKnmStatusApproving && isSectionRequiredForNextStatusesInApproving(sectionName);
        const isShowTypeWaitingCarriedOutMark =
          isSupervisor &&
          isKnmStatusTypeWaitingCarriedOut &&
          isFieldRequiredForNextStatusesInTypeWaitingCarriedOut(
            fieldName,
            props.values,
            !!isAvailableUnplannedActivities,
          );
        const isShowTypeCarriedOutMark =
          isSupervisor &&
          isKnmStatusTypeCarriedOut &&
          isFieldRequiredForNextStatusesInTypeCarriedOut(fieldName, props.values, isEnablePaperlessPassportDocument);

        const isShowRedMark = isShowRequiredMark;
        const isShowYellowMark =
          isShowFillInProcessMark ||
          isShowCommentsExistMark ||
          isShowTypeCarriedOutMark ||
          isShowApprovingMark ||
          isShowReadyForApproveMark ||
          isShowTypeWaitingCarriedOutMark;

        const field = form.getFieldState(fieldName as keyof IKnmForm);
        const isError = typeof field?.error === 'string';
        const isEmpty = !field?.value || (Array.isArray(field?.value) && !field?.value.length);

        if (
          fieldName &&
          isShowRedMark &&
          isError &&
          (isEmpty || fieldName === 'approved' || field?.error === REQUIRED_ERROR)
        ) {
          return <span className={styles.RedMark}> *</span>;
        }

        if (fieldName && isShowYellowMark && isEmpty) {
          return <span className={styles.YellowMark}> *</span>;
        }

        if (sectionName && isShowYellowMark) {
          return <span className={styles.YellowMark}> *</span>;
        }

        return null;
      }}
    </FormSpy>
  );
};
